import type React from 'react';
import { useEffect, useState } from 'react';
import { authenticatedFetch } from '../utils/api';

interface SponsoredResult {
  id: string;
  keywords: string[];
  title: string;
  description: string;
  url?: string;
  forceResult?: {
    index: string;
    resultId: string;
    resultTitle: string;
  };
  category: 'sponsored' | 'targeted' | 'priority';
  startDate: string;
  endDate: string;
  priority: number;
  active: boolean;
  createdAt: string;
}

interface OpenSearchResult {
  _id: string;
  _index: string;
  _source: {
    title?: string;
    name?: string;
    description?: string;
    url?: string;
    [key: string]: any;
  };
}

interface PromotedResultFormProps {
  editingResult?: SponsoredResult | null;
  onSave: () => void;
  onCancel: () => void;
}

const PromotedResultForm: React.FC<PromotedResultFormProps> = ({ editingResult, onSave, onCancel }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<OpenSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const API_BASE_URL = import.meta.env.VITE_API_URL || '';

  const [formData, setFormData] = useState<Partial<SponsoredResult> & { keywordsText?: string }>({
    keywords: [],
    title: '',
    description: '',
    url: '',
    forceResult: undefined,
    category: 'sponsored',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    priority: 1,
    active: true,
  });

  useEffect(() => {
    if (editingResult) {
      setFormData({
        ...editingResult,
        keywordsText: editingResult.keywords?.join(', ') || '',
      });
    }
  }, [editingResult]);

  const searchOpenSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/autocomplete?query=${encodeURIComponent(query)}&size=10`);
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.hits?.hits || []);
      }
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Add debouncing with useEffect
  useEffect(() => {
    if (searchQuery.length > 2) {
      const debounceTimeout = setTimeout(() => {
        searchOpenSearch(searchQuery);
      }, 300);

      return () => clearTimeout(debounceTimeout);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const handleSearchInputChange = (value: string) => {
    setSearchQuery(value);
  };

  const selectForceResult = (result: OpenSearchResult) => {
    setFormData({
      ...formData,
      forceResult: {
        index: result._index,
        resultId: result._id,
        resultTitle: result._source.title || result._source.name || 'Untitled',
      },
    });
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleKeywordChange = (value: string) => {
    // Store the raw text string for display
    setFormData({ ...formData, keywordsText: value });

    // Split by comma and clean up each keyword for the actual keywords array
    const keywords = value
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k.length > 0);

    // No limit on number of keywords, but warn if getting very large
    if (keywords.length > 100) {
      console.warn(
        `Large number of keywords: ${keywords.length}. Consider being more selective for better performance.`
      );
    }

    setFormData({ ...formData, keywords, keywordsText: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const resultData: SponsoredResult = {
      id: editingResult?.id || Date.now().toString(),
      keywords: formData.keywords || [],
      title: formData.title || '',
      description: formData.description || '',
      url: formData.url,
      forceResult: formData.forceResult,
      category: formData.category || 'sponsored',
      startDate: formData.startDate || new Date().toISOString().split('T')[0],
      endDate: formData.endDate || '',
      priority: formData.priority || 1,
      active: formData.active !== false,
      createdAt: editingResult?.createdAt || new Date().toISOString(),
    };

    setIsSaving(true);
    try {
      const method = editingResult ? 'PUT' : 'POST';
      const response = await authenticatedFetch(`${API_BASE_URL}/api/sponsored-results`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(resultData),
      });

      if (response.ok) {
        onSave();
      }
    } catch (error) {
      console.error('Failed to save sponsored result:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto cursor-pointer">
      <div className="flex justify-between items-center mb-8 pb-4 border-b border-zinc-200 cursor-pointer">
        <h2 className="text-2xl font-semibold text-zinc-900 cursor-pointer">
          {editingResult ? 'Edit' : 'Add'} Managed Result
        </h2>
        <div className="flex gap-3 cursor-pointer">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-zinc-300 text-zinc-700 px-4 py-2 text-sm font-medium hover:bg-zinc-50 hover:border-zinc-400 transition-all cursor-pointer"
          >
            Cancel
          </button>
          <button
            form="promoted-result-form"
            type="submit"
            className="rounded-lg bg-zinc-900 text-white px-4 py-2 text-sm font-medium hover:bg-zinc-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : editingResult ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      <div className="space-y-6 cursor-pointer">
        <form id="promoted-result-form" onSubmit={handleSubmit} className="space-y-6 cursor-pointer">
          {/* Basic Settings */}
          <div className="border-t border-zinc-100 pt-6 space-y-6 cursor-pointer">
            <h4 className="text-base font-semibold text-zinc-900 cursor-pointer">Basic Configuration</h4>

            <div className="space-y-2 cursor-pointer">
              <label htmlFor="category" className="block text-sm font-medium text-zinc-700 cursor-pointer">
                Category
                <span className="ml-1 text-xs font-normal text-zinc-500 cursor-pointer">Choose display style</span>
              </label>
              <select
                id="category"
                value={formData.category || 'sponsored'}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as 'sponsored' | 'targeted' | 'priority' })}
                required
                className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent bg-white appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27currentColor%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%27%3e%3c/polyline%3e%3c/svg%3e')] bg-[length:1.25rem] bg-[right_0.5rem_center] bg-no-repeat pr-10 cursor-pointer"
              >
                <option value="sponsored">Sponsored (appears in promoted section with prominent badge)</option>
                <option value="targeted">Promoted (appears in promoted section with subtle badge)</option>
                <option value="priority">Priority (appears as first regular results, no badge)</option>
              </select>
              {formData.category === 'priority' && (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                  <strong>Note:</strong> Priority results require selecting a "Force Specific Result" below. They will appear as the first regular search results in priority order (1, 2, 3...).
                </div>
              )}
            </div>

            <div className="space-y-2 cursor-pointer">
              <label htmlFor="keywords" className="block text-sm font-medium text-zinc-700 cursor-pointer">
                Keywords
                <span className="ml-1 text-xs font-normal text-zinc-500 cursor-pointer">
                  Comma-separated trigger words (unlimited)
                </span>
              </label>
              <textarea
                id="keywords"
                value={formData.keywordsText || formData.keywords?.join(', ') || ''}
                onChange={(e) => handleKeywordChange(e.target.value)}
                placeholder="steel, metal, manufacturing, heat treatment, alloys, tempering, annealing, quenching"
                rows={3}
                required
                className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent resize-y min-h-[5rem] leading-relaxed cursor-pointer"
              />
              <small className="block text-xs text-zinc-500 mt-1 cursor-pointer">
                {formData.keywords?.length || 0} keywords entered
              </small>
            </div>
          </div>

          {/* Content */}
          <div className="border-t border-zinc-100 pt-6 space-y-6 cursor-pointer">
            <h4 className="text-base font-semibold text-zinc-900 cursor-pointer">Content</h4>

            <div className="space-y-2 cursor-pointer">
              <label htmlFor="title" className="block text-sm font-medium text-zinc-700 cursor-pointer">
                Title
              </label>
              <input
                type="text"
                id="title"
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter a compelling title"
                required
                className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent cursor-pointer"
              />
            </div>

            <div className="space-y-2 cursor-pointer">
              <label htmlFor="description" className="block text-sm font-medium text-zinc-700 cursor-pointer">
                Description
              </label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                placeholder="Provide a clear description of what users will find"
                required
                className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent resize-y min-h-[5rem] leading-relaxed cursor-pointer"
              />
            </div>

            <div className="space-y-2 cursor-pointer">
              <label htmlFor="url" className="block text-sm font-medium text-zinc-700 cursor-pointer">
                URL
                <span className="ml-1 text-xs font-normal text-zinc-500 cursor-pointer">Optional destination link</span>
              </label>
              <input
                type="url"
                id="url"
                value={formData.url || ''}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://example.com"
                className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent cursor-pointer"
              />
            </div>
          </div>

          {/* Force Result Section */}
          <div className="border-t border-zinc-100 pt-6 space-y-6 cursor-pointer">
            <h4 className="text-base font-semibold text-zinc-900 cursor-pointer">Force Specific Result (Optional)</h4>

            <div className="space-y-2 cursor-pointer">
              <label htmlFor="search-result" className="block text-sm font-medium text-zinc-700 cursor-pointer">
                Search for existing content
                <span className="ml-1 text-xs font-normal text-zinc-500 cursor-pointer">
                  Override custom content with existing OpenSearch result
                </span>
              </label>
              <div className="relative cursor-pointer">
                <input
                  type="text"
                  id="search-result"
                  value={searchQuery}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                  placeholder="Search OpenSearch results..."
                  className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent cursor-pointer"
                />
                {isSearching && (
                  <div className="py-2 px-3 text-center text-zinc-500 text-sm cursor-pointer">Searching...</div>
                )}
                {searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-zinc-300 border-t-0 rounded-b-md max-h-72 overflow-y-auto z-10 shadow-lg cursor-pointer">
                    {searchResults.map((result) => (
                      <div
                        key={result._id}
                        className="py-2 px-3 cursor-pointer border-b border-zinc-100 hover:bg-zinc-50 transition-colors last:border-b-0"
                        onClick={() => selectForceResult(result)}
                      >
                        <div className="font-medium text-zinc-900 mb-0.5">
                          {result._source.title || result._source.name || 'Untitled'}
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs text-zinc-500 mb-1">
                          <span className="bg-zinc-100 px-1.5 py-0.5 rounded capitalize">{result._index.replace('asmi-', '').replace(/-/g, ' ')}</span>
                          {result._source.sku && <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">SKU: {result._source.sku}</span>}
                          {result._source.isbn && <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">ISBN: {result._source.isbn}</span>}
                          {result._source.doi && <span className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">DOI</span>}
                        </div>
                        {(result._source.authors || result._source.author) && (
                          <div className="text-xs text-zinc-400 truncate">
                            By: {Array.isArray(result._source.authors) ? result._source.authors.slice(0, 3).join(', ') : (result._source.authors || result._source.author)}
                          </div>
                        )}
                        {result._source.url && (
                          <div className="text-xs text-zinc-400 truncate mt-0.5">
                            {result._source.url}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {formData.forceResult && (
                <div className="flex justify-between items-center bg-green-50 border border-green-200 rounded-lg px-3 py-2 mt-2 cursor-pointer">
                  <span className="text-green-800 font-medium text-sm cursor-pointer">
                    Selected: {formData.forceResult.resultTitle}
                  </span>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, forceResult: undefined })}
                    className="text-red-600 hover:bg-red-50 text-xs px-2 py-1 rounded transition-colors cursor-pointer"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Scheduling & Priority */}
          <div className="border-t border-zinc-100 pt-6 space-y-6 cursor-pointer">
            <h4 className="text-base font-semibold text-zinc-900 cursor-pointer">Scheduling & Priority</h4>

            <div className="grid grid-cols-2 gap-5 cursor-pointer">
              <div className="space-y-2 cursor-pointer">
                <label htmlFor="startDate" className="block text-sm font-medium text-zinc-700 cursor-pointer">
                  Start Date
                </label>
                <input
                  type="date"
                  id="startDate"
                  value={formData.startDate || ''}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  required
                  className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent cursor-pointer"
                />
              </div>

              <div className="space-y-2 cursor-pointer">
                <label htmlFor="endDate" className="block text-sm font-medium text-zinc-700 cursor-pointer">
                  End Date
                  <span className="ml-1 text-xs font-normal text-zinc-500 cursor-pointer">
                    Leave blank for no expiration
                  </span>
                </label>
                <input
                  type="date"
                  id="endDate"
                  value={formData.endDate || ''}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent cursor-pointer"
                />
              </div>
            </div>

            <div className="grid grid-cols-[2fr_1fr_1fr] gap-5 items-end cursor-pointer">
              <div className="space-y-2 cursor-pointer">
                <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-zinc-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.active !== false}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="rounded border-zinc-300 text-zinc-900 focus:ring-2 focus:ring-zinc-900 cursor-pointer"
                  />
                  Active
                  <span className="text-xs font-normal text-zinc-500 cursor-pointer">Enable/disable this result</span>
                </label>
              </div>
              <div className="space-y-2 cursor-pointer">
                <label htmlFor="priority" className="block text-sm font-medium text-zinc-700 cursor-pointer">
                  Priority
                  <span className="ml-1 text-xs font-normal text-zinc-500 cursor-pointer">
                    1-10 (higher appears first)
                  </span>
                </label>
                <input
                  type="number"
                  id="priority"
                  min="1"
                  max="10"
                  value={formData.priority || 1}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value, 10) })}
                  className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent cursor-pointer"
                />
              </div>
              <div></div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-5 border-t border-zinc-200 bg-zinc-50 -mx-6 px-6 -mb-6 pb-6 rounded-b-lg cursor-pointer">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-zinc-300 text-zinc-700 px-4 py-2 text-sm font-medium hover:bg-white hover:border-zinc-400 transition-all cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-lg bg-zinc-900 text-white px-4 py-2 text-sm font-medium hover:bg-zinc-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : editingResult ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PromotedResultForm;
